import React, { useCallback, useState } from 'react';
import { CalendarDaysIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { releaseNotes } from '../data/releaseNotes';
import { releaseTutorials } from '../data/releaseTutorials';
import { ReleaseNoteDetailPanel, type ReleaseSelection } from './ReleaseNoteDetailPanel';
import { MODULE_LABELS, TYPE_STYLES } from './releaseNoteStyles';
import { getTryTarget, type FeatureTarget } from '../utils/releaseTryTarget';
interface ReleaseNotesModuleProps {
  onTryFeature?: (target: FeatureTarget) => void;
}
export function ReleaseNotesModule({
  onTryFeature
}: ReleaseNotesModuleProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [selection, setSelection] = useState<ReleaseSelection | null>(null);
  const formatDate = useCallback((iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }), [lang]);
  const close = useCallback(() => setSelection(null), []);
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5314" data-mp="3oav5piu5ws59">
      <div className="mb-8" data-id="element-5315" data-mp="3od8x9hpmvm82">
        <h1 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-5316" data-mp="3sczwm9t87ao7">
          {t('Notes de version', 'Release notes')}
        </h1>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5317" data-mp="1784j4vw1nyr">
          {t('Les nouveautés et améliorations des dernières versions de bciti+.', 'What’s new and improved in the latest bciti+ releases.')}
        </p>
      </div>

      <ol className="flex flex-col gap-6" data-id="element-5318" data-mp="fvl4ecy3jqyj">
        {releaseNotes.map((release, i) => <li key={release.version} data-id="element-5319" data-mp="jwwx4rc9osay" data-mpi={i}>
            <article className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-5320" data-mp="jvv97ma5kbbt">
              <header className="px-6 py-5 border-b border-slate-100" data-id="element-5321" data-mp="ox2glk8dv8hy">
                <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-5322" data-mp="tuuhv4ux46yt">
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-5323" data-mp="tsrivvbsf3hy">
                    <CalendarDaysIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-5324" data-mp="2pw6479n2cb2y" />
                    <time dateTime={release.date} data-id="element-5325" data-mp="2l0z40bt05ois">{formatDate(release.date)}</time>
                  </span>
                  {i === 0 && <span className="px-2 py-0.5 rounded-md bg-[#4F46E5] text-xs font-semibold text-white" data-id="element-5326" data-mp="2uxlogzhf2dka">
                      {t('Dernière version', 'Latest')}
                    </span>}
                </div>
                <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5327" data-mp="34m3ld5mks0in">{release.title[lang]}</h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-5328" data-mp="3illd9tv63tw8">{release.summary[lang]}</p>
              </header>
              <ul className="py-2" data-id="element-5329" data-mp="3dp4merwl31fg">
                {release.changes.map((c, j) => {
              const style = TYPE_STYLES[c.type];
              const Icon = style.Icon;
              const tutorial = releaseTutorials[`${release.version}:${j}`];
              return <li key={j} data-id="element-5330" data-mp="3uygpyony6k4f" data-mpi={j}>
                      <button type="button" onClick={() => setSelection({
                  release,
                  change: c,
                  tutorial,
                  tryTarget: getTryTarget(release.version, j, c)
                })} className="group w-full px-6 py-3 flex flex-col sm:flex-row items-start gap-2 sm:gap-3 text-left hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4F46E5] transition-colors" data-id="element-5331" data-mp="3uz8ljzhjohup">
                        <span className="shrink-0 flex items-center gap-2 sm:w-[200px] mt-0.5" data-id="element-5332" data-mp="329jlbocnslu5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${style.className}`} data-id="element-5333" data-mp="378mdw9bgczl8">
                            <Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-5334" data-mp="15f59deimceup" />
                            {style[lang]}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-5335" data-mp="31h08kllk4h60">{MODULE_LABELS[c.module][lang]}</span>
                        </span>
                        <span className="flex-1 min-w-0 text-sm leading-relaxed" data-id="element-5336" data-mp="2shvcdlxgwzx0">
                          <span className="block font-semibold text-[#0F172A]" data-id="element-5337" data-mp="2njtsiys7wwkh">{c.title[lang]}</span>
                          <span className="block text-slate-600" data-id="element-5338" data-mp="2ik8klyv2ldvm">{c.text[lang]}</span>
                        </span>
                        <ChevronRightIcon className="hidden sm:block shrink-0 w-4 h-4 mt-1 text-slate-300 group-hover:text-slate-500 transition-colors" aria-hidden="true" data-id="element-5339" data-mp="2dticidv9ewx8" />
                      </button>
                    </li>;
            })}
              </ul>
            </article>
          </li>)}
      </ol>

      <ReleaseNoteDetailPanel selection={selection} onClose={close} formatDate={formatDate} onTry={onTryFeature ? target => {
      setSelection(null);
      onTryFeature(target);
    } : undefined} data-id="element-5340" data-mp="1uvxota48e4e7" />
    </div>;
}